import React, { FC, useEffect, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";

import Button from "../../components/button/Button";
import { OutlineButton } from "../../components/button/OutlineButton";
import { StyledDatePicker } from "../../components/datepicker/datepicker";
import InputTime from "../../components/inputs/inputTime";
import CustomSelect from "../../components/select/Select";
import { getInitialsFromFullName } from "../../constants/constants";
import { useAppDispatch, useAppSelector } from "../../redux/redux";
import { addTime } from "../../services/AppService";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { useGetSearchCustomersQuery } from "../../slices/ContactsSlice";
import { useGetSearchCustomersWorksQuery } from "../../slices/WorkSlice";
import { useGetWorkTypesQuery } from "../../slices/WorkTypeSlice";
import { SelectOptionType } from "../../types/types";
import type { AddTimeInitial } from "../ai/ask/actionProposal.utils";
import {
  ADD_TIME_FUTURE,
  addTimeProblem,
  billableDefaultFor,
  durationSeconds,
  entryDateIso,
} from "./addTime.utils";
import { BillableSwitch } from "./BillableSwitch";
import { EDIT_TIME_DATE_PROBLEM } from "./editTime.utils";

type Props = {
  closeModal: () => void;
  /** Shows "Start a timer instead" (Create > New Time). */
  onStartTimer?: () => void;
  onSaved?: () => void;
  /** Ask ProCharted "add time": the window opens filled in (nothing is saved until Save). */
  initial?: AddTimeInitial;
};

const FIELD_LABEL_CLASS = "label-text mb-2 text-sm font-medium";

/**
 * "Add time" (QA batch 15, TIME-015): time already worked — client, work item,
 * date, hours/minutes, note, billable (starts at the work type's default like
 * the timer). Saved with `POST api/time/add-time`.
 */
const AddTimeEntry: FC<Props> = ({
  closeModal,
  onStartTimer,
  onSaved,
  initial,
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.appReducer);
  const orgId = getLinkedFirm()?.orgId || "";

  const [searchContacts, setSearchContacts] = useState("");
  const [contact, setContact] = useState<SelectOptionType | null>(
    initial?.contact ?? null,
  );
  const [work, setWork] = useState<SelectOptionType | null>(
    initial?.work ?? null,
  );
  const [date, setDate] = useState<Date | null>(initial?.date ?? new Date());
  const [dateError, setDateError] = useState("");
  const [duration, setDuration] = useState({
    h: initial?.hours ?? "",
    m: initial?.minutes ?? "",
  });
  const [durationError, setDurationError] = useState("");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [isBillable, setIsBillable] = useState(initial?.isBillable ?? true);
  // The proposal already applied the work type's default (or what was asked).
  const [billableTouched, setBillableTouched] = useState(!!initial);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [triedSave, setTriedSave] = useState(false);

  const searchDebounce = useDebounce(searchContacts, 50);
  const { data: contacts, isFetching: isLoadingContacts } =
    useGetSearchCustomersQuery({
      searchQuery: searchDebounce || "ab",
      orgId,
      userId: user.id,
      isActive: true,
    });
  const { data: works, isFetching: isLoadingWorks } =
    useGetSearchCustomersWorksQuery(
      { customerId: contact?.value || "", orgId },
      { skip: !contact?.value },
    );
  const { data: workTypes } = useGetWorkTypesQuery(
    { orgId, status: "all" },
    { skip: !orgId },
  );

  const contactOptions = useMemo(
    (): SelectOptionType[] =>
      (contacts || []).map((item) => ({
        value: item?.customerId || "",
        label: `${item.customerName}`,
        avatar: getInitialsFromFullName(item?.customerName || ""),
        avatarColor: item.businessContactId ? "purple" : "blue",
      })),
    [contacts],
  );

  // Billable starts at the work type's default until the person changes it.
  useEffect(() => {
    if (!billableTouched) {
      setIsBillable(billableDefaultFor(work?.workTypeId, workTypes));
    }
  }, [work?.workTypeId, workTypes, billableTouched]);

  const problem = addTimeProblem({ contact, work, date, duration });

  const onSave = async () => {
    setTriedSave(true);
    if (problem || !work?.value || !date) return;
    setIsSaving(true);
    setError("");
    const result = await dispatch(
      addTime({
        workId: work.value,
        assignedUserId: user.id,
        date: entryDateIso(date),
        duration: durationSeconds(duration),
        notes: notes.trim() || null,
        isBillable,
      }),
    );
    setIsSaving(false);
    if (addTime.rejected.match(result)) {
      setError(
        (result.payload as string) || "Couldn't save the time. Try again.",
      );
      return;
    }
    onSaved?.();
    closeModal();
  };

  const shownProblem =
    error ||
    (triedSave && problem && problem !== dateError && problem !== durationError
      ? problem
      : "");

  return (
    <div
      onClick={closeModal}
      id={"add-time-modal"}
      className={
        "fixed left-0 top-0 z-[4] flex h-screen w-screen items-center justify-center bg-gray-500/20 px-4"
      }>
      <div
        role={"dialog"}
        aria-label={"Add time"}
        className={
          "max-h-[90vh] w-full max-w-[480px] overflow-auto rounded-2xl bg-white p-6"
        }
        onClick={(event) => event.stopPropagation()}>
        <div className={"mb-1 text-center text-[18px] font-bold"}>Add time</div>
        <div className={"mb-6 text-center text-sm text-[#6B7280]"}>
          Time you already worked.
          {onStartTimer && (
            <>
              {" "}
              <button
                type={"button"}
                className={"font-semibold text-[#6B4EFF] hover:underline"}
                onClick={onStartTimer}>
                Start a timer instead
              </button>
            </>
          )}
        </div>

        <div className={"relative z-[2] mb-4"}>
          <div className={FIELD_LABEL_CLASS}>Client</div>
          <CustomSelect
            options={contactOptions}
            value={contact}
            onChange={(newValue) => {
              const next = newValue as SelectOptionType;
              if (next?.value !== contact?.value) setWork(null);
              setContact(next);
            }}
            inputValue={searchContacts}
            onChangeInput={setSearchContacts}
            withAvatar
            withMenu
            isLoading={isLoadingContacts}
            placeholder={"Search clients..."}
            selectStyle={{ fullWidth: true, width: "432px" }}
          />
        </div>

        <div className={"relative z-[1] mb-4"}>
          <div className={FIELD_LABEL_CLASS}>Work item</div>
          <CustomSelect
            options={works || []}
            value={work}
            onChange={(newValue) => setWork(newValue as SelectOptionType)}
            withMenu
            isLoading={isLoadingWorks}
            placeholder={contact ? "Search..." : "Pick a client first"}
            selectStyle={{ fullWidth: true, width: "432px" }}
          />
        </div>

        <div className={"mb-4 grid grid-cols-[minmax(170px,1fr)_1fr] gap-4"}>
          <div>
            <div className={FIELD_LABEL_CLASS}>Date</div>
            <StyledDatePicker
              value={date}
              format={"MMM d, yyyy"}
              placeholder={"MMM D, YYYY"}
              mountNode={"add-time-modal"}
              size={"sm"}
              showCalendarIcon
              acceptTypedDates
              maxDate={new Date()}
              onChange={(next) => {
                setDate(next);
                setDateError("");
              }}
              onClear={() => {
                setDate(null);
                setDateError(EDIT_TIME_DATE_PROBLEM);
              }}
              onInvalidTyped={(reason) => {
                setDate(null);
                setDateError(
                  reason === "out-of-range"
                    ? ADD_TIME_FUTURE
                    : EDIT_TIME_DATE_PROBLEM,
                );
              }}
            />
            {dateError && (
              <div className={"pt-2 text-sm font-bold text-error"}>
                {dateError}
              </div>
            )}
          </div>
          <div>
            <div className={FIELD_LABEL_CLASS}>Hours and minutes</div>
            <InputTime
              value={duration}
              onChange={(next) => {
                setDuration(next);
                setDurationError("");
              }}
              onProblem={(message) => setDurationError(message)}
              inputClassName={"h-8 text-sm font-medium"}
            />
            {durationError && (
              <div className={"pt-2 text-sm font-bold text-error"}>
                {durationError}
              </div>
            )}
          </div>
        </div>

        <BillableSwitch
          value={isBillable}
          onChange={(value) => {
            setBillableTouched(true);
            setIsBillable(value);
          }}
          className={"mb-4"}
        />

        <div className={"mb-4"}>
          <div className={FIELD_LABEL_CLASS}>Note</div>
          <textarea
            value={notes}
            maxLength={2000}
            placeholder={"What did you work on? (optional)"}
            className={
              "input input-bordered min-h-[80px] w-full py-[8px] text-sm font-medium placeholder:text-gray-400"
            }
            onChange={(event) => setNotes(event.target.value)}
          />
        </div>

        {shownProblem && (
          <div className={"mb-3 text-sm font-bold text-error"} role={"alert"}>
            {shownProblem}
          </div>
        )}

        <div className={"flex justify-end space-x-[10px]"}>
          <OutlineButton
            buttonType={"button"}
            label={"Cancel"}
            colorType={"neutral"}
            onClick={closeModal}
            extraClasses={"normal-case"}
          />
          <Button
            buttonType={"button"}
            label={"Save time"}
            onClick={onSave}
            isLoading={isSaving}
            disabled={isSaving}
            extraClasses={"normal-case"}
          />
        </div>
      </div>
    </div>
  );
};

export default AddTimeEntry;
